import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useResource } from '../lib/useResource.js';
import { post } from '../lib/api.js';
import { formatBRL, formatDate, formatDateTime, formatPhone, vehicleLabel } from '../lib/format.js';
import {
  Alert,
  Badge,
  Card,
  Empty,
  ErrorBox,
  Field,
  Modal,
  PageHeader,
  useSubmit,
  useToast,
} from '../components/ui.js';
import { useSession } from '../lib/session.js';
import { NovoLembrete } from './reminders/NovoLembrete.js';

interface Customer {
  id: string;
  name: string;
  phone_e164: string | null;
  tags: string[];
  notes: string | null;
  status: string;
  source: string;
  created_at: string;
}
interface Vehicle {
  id: string;
  plate: string | null;
  make: string | null;
  model: string | null;
  model_year: number | null;
  status: string;
}
interface ServiceRow {
  id: string;
  record_type: string;
  purpose: string;
  performed_on: string;
  odometer_km: number | null;
  total_cents: number | null;
  notes: string | null;
}
interface Cycle {
  id: string;
  status: string;
  purpose: string;
  next_send_at: string | null;
  pause_reason: string | null;
  awaiting_reason: string | null;
}

export function CustomerDetail() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const toast = useToast();
  const [novoVeiculo, setNovoVeiculo] = useState(false);
  const [novoLembrete, setNovoLembrete] = useState(false);

  const lista = useResource<{ customers: Customer[] }>(`/api/customers?term=`);
  const vehicles = useResource<{ vehicles: Vehicle[] }>(`/api/customers/${id}/vehicles`);
  const services = useResource<{ services: ServiceRow[] }>(`/api/customers/${id}/services`);
  const upcoming = useResource<{ cycles: (Cycle & { customer_name: string })[] }>('/api/reminders/upcoming');

  const customer = lista.data?.customers.find((c) => c.id === id) ?? null;
  const cycles = (upcoming.data?.cycles ?? []).filter(() => true);

  return (
    <>
      <PageHeader
        title={customer?.name ?? 'Ficha do cliente'}
        description={
          customer ? (
            <>
              {formatPhone(customer.phone_e164)} · cliente desde {formatDate(customer.created_at)}
            </>
          ) : null
        }
        actions={
          <>
            <Link className="button" to="/clientes">
              Voltar
            </Link>
            {can('reminders.write') ? (
              <button type="button" onClick={() => setNovoLembrete(true)}>
                Novo lembrete
              </button>
            ) : null}
            {can('services.write') ? (
              <Link className="button button-primary" to={`/servicos/novo?cliente=${id}`}>
                Registrar serviço
              </Link>
            ) : null}
          </>
        }
      />

      <ErrorBox error={lista.error ?? vehicles.error ?? services.error} />

      <div className="grid cols-2">
        <div className="stack">
          <Card
            title="Veículos"
            actions={
              can('customers.write') ? (
                <button type="button" className="small" onClick={() => setNovoVeiculo(true)}>
                  Adicionar veículo
                </button>
              ) : null
            }
            tight
          >
            {vehicles.data && vehicles.data.vehicles.length === 0 ? (
              <Empty title="Nenhum veículo vinculado.">
                O lembrete avulso funciona mesmo sem veículo cadastrado.
              </Empty>
            ) : (
              <ul className="list">
                {(vehicles.data?.vehicles ?? []).map((vehicle) => (
                  <li key={vehicle.id}>
                    <div className="list-main">
                      <div className="list-title">{vehicleLabel(vehicle)}</div>
                      <div className="list-sub">
                        {vehicle.status === 'sold' ? 'Veículo vendido' : 'Proprietário atual'}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Histórico de serviços" tight>
            {services.data && services.data.services.length === 0 ? (
              <Empty title="Nenhum serviço registrado." />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Tipo</th>
                      <th>Hodômetro</th>
                      <th className="num">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(services.data?.services ?? []).map((service) => (
                      <tr key={service.id}>
                        <td className="nowrap">{formatDate(service.performed_on)}</td>
                        <td>
                          {service.record_type === 'performed' ? (
                            <Badge tone="ok">feito aqui</Badge>
                          ) : (
                            <Badge tone="attention">relato externo</Badge>
                          )}
                        </td>
                        <td className="num">
                          {service.odometer_km ? `${service.odometer_km.toLocaleString('pt-BR')} km` : '—'}
                        </td>
                        <td className="num">{formatBRL(service.total_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="stack">
          <Card title="Retornos programados" tight>
            {cycles.length === 0 ? (
              <Empty title="Nenhum retorno programado." />
            ) : (
              <ul className="list">
                {cycles.map((cycle) => (
                  <li key={cycle.id}>
                    <div className="list-main">
                      <div className="list-title">{cycle.purpose.replace('standalone:', 'lembrete avulso')}</div>
                      <div className="list-sub">
                        {cycle.status === 'scheduled' ? 'Programado' : 'Em andamento'}
                      </div>
                    </div>
                    <span className="list-when">{formatDateTime(cycle.next_send_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Consentimento de lembretes">
            <Consentimento customerId={id} onDone={() => toast('Consentimento registrado.')} />
          </Card>

          {customer?.notes ? (
            <Card title="Observações">
              <p className="small">{customer.notes}</p>
            </Card>
          ) : null}
        </div>
      </div>

      {novoVeiculo ? (
        <NovoVeiculo
          customerId={id}
          onClose={() => setNovoVeiculo(false)}
          onCreated={() => {
            setNovoVeiculo(false);
            vehicles.reload();
            toast('Veículo cadastrado.');
          }}
        />
      ) : null}

      {novoLembrete ? (
        <NovoLembrete
          customerId={id}
          vehicles={vehicles.data?.vehicles ?? []}
          onClose={() => setNovoLembrete(false)}
          onCreated={() => {
            setNovoLembrete(false);
            upcoming.reload();
            toast('Lembrete criado.');
          }}
        />
      ) : null}
    </>
  );
}

function Consentimento({ customerId, onDone }: { customerId: string; onDone: () => void }) {
  const [registrado, setRegistrado] = useState(false);
  const { submit, saving, error } = useSubmit(async () => {
    await post(`/api/customers/${customerId}/consent`, {
      purpose: 'reminders',
      status: 'granted',
      source: 'panel',
      textShown: 'Aceito receber lembretes de manutenção da Auto Center GL.',
    });
    setRegistrado(true);
    onDone();
  });

  return (
    <form onSubmit={submit}>
      <ErrorBox error={error} />
      <p className="small muted">
        O lembrete só é enviado com consentimento registrado. Contato importado não ganha aceite
        automático.
      </p>
      {registrado ? (
        <Alert tone="info">Consentimento registrado agora.</Alert>
      ) : (
        <button type="submit" disabled={saving}>
          {saving ? 'Registrando…' : 'Registrar aceite para lembretes'}
        </button>
      )}
    </form>
  );
}

function NovoVeiculo({
  customerId,
  onClose,
  onCreated,
}: {
  customerId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [plate, setPlate] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');

  const { submit, saving, error } = useSubmit(async () => {
    await post('/api/vehicles', {
      customerId,
      plate: plate || null,
      make: make || null,
      model: model || null,
      modelYear: year ? Number(year) : null,
    });
    onCreated();
  });

  return (
    <Modal
      title="Adicionar veículo"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-veiculo" className="primary" disabled={saving}>
            {saving ? 'Salvando…' : 'Adicionar'}
          </button>
        </>
      }
    >
      <form id="form-veiculo" onSubmit={submit}>
        <ErrorBox error={error} />
        <div className="field-row">
          <Field label="Placa" hint="Opcional. ABC1234 ou ABC1D23.">
            <input type="text" value={plate} onChange={(e) => setPlate(e.target.value)} />
          </Field>
          <Field label="Marca">
            <input type="text" value={make} onChange={(e) => setMake(e.target.value)} />
          </Field>
        </div>
        <div className="field-row">
          <Field label="Modelo">
            <input type="text" value={model} onChange={(e) => setModel(e.target.value)} />
          </Field>
          <Field label="Ano">
            <input type="number" value={year} onChange={(e) => setYear(e.target.value)} min={1900} max={2100} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
